import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Calendar,
  FileText,
  Check,
  XCircle,
  Loader2,
  Landmark,
  User,
  Receipt,
  Send,
  BellRing,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ApprovalTimeline from "../advance/components/ApprovalTimeline";
import PaymentDocumentPaper from "./PaymentDocumentPaper";
import { STATUS_MAP } from "../advance/AdvanceDocumentPaper";
import { formatDateVN } from "../../../utils/formatUtils";
import {
  getPaymentDetail,
  remindPaymentApprovers,
} from "../../../api/finance/payment/paymentRequests";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";

const PaymentDetailModal = ({
  isOpen,
  onClose,
  paymentId,
  onUpdateStatus,
  onReminded,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [remindLoading, setRemindLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const storedUser = localStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser) : null;

  useEffect(() => {
    if (!isOpen || !paymentId) return;

    const loadDetail = async () => {
      try {
        setLoading(true);
        const res = await getPaymentDetail(paymentId);
        setData(res);
      } catch (err) {
        console.error("Load payment detail error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [isOpen, paymentId]);

  useEffect(() => {
    if (!isOpen) {
      setData(null);
      setRejectReason("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await onUpdateStatus(paymentId, "approved");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Vui lòng nhập lý do từ chối");
      return;
    }
    try {
      setActionLoading(true);
      await onUpdateStatus(paymentId, "rejected", rejectReason);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitDraft = async () => {
    try {
      setActionLoading(true);
      await onUpdateStatus(paymentId, "submitted");
    } finally {
      setActionLoading(false);
    }
  };

  const canCurrentUserRemind =
    data?.status === "waiting" && data?.employee_id === currentUser?.id;

  const handleRemind = async () => {
    if (!paymentId) return;
    try {
      setRemindLoading(true);
      const res = await remindPaymentApprovers(paymentId);
      showSuccessToast(
        res?.message || "Đã gửi thông báo nhắc duyệt đến người phê duyệt."
      );
      setData((prev) =>
        prev ? { ...prev, is_urgent: true, urgent_at: new Date().toISOString() } : prev
      );
      onReminded?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Không thể gửi nhắc duyệt");
    } finally {
      setRemindLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const { label, cls } = STATUS_MAP[status] || {
      label: status,
      cls: "bg-gray-50 text-gray-600 border-gray-100",
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${cls}`}
      >
        {label}
      </span>
    );
  };

  const code = data?.request_code || data?.payment_code;
  const lines =
    Array.isArray(data?.lines) && data.lines.length > 0
      ? data.lines
      : data?.payment_reason
        ? [
            {
              line_no: 1,
              description: data.payment_reason,
              amount: data.payment_amount,
            },
          ]
        : [];

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex flex-col bg-white md:bg-slate-900/55 md:backdrop-blur-sm md:items-center md:justify-center md:p-5"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 36, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 36, scale: 0.98 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full h-full min-h-0 md:max-w-6xl md:h-[90vh] md:rounded-[1.75rem] shadow-2xl overflow-hidden flex flex-col border-0 md:border md:border-slate-200"
        >
          <div className="px-4 py-3 md:px-6 md:py-4 border-b border-slate-200 flex items-center justify-between shrink-0 bg-white pt-[max(0.75rem,env(safe-area-inset-top))]">
            <div className="min-w-0">
              <h2 className="text-base md:text-lg font-black text-slate-800 uppercase tracking-tight">
                Chi tiết <span className="text-red-700">thanh toán</span>
              </h2>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5 truncate">
                {code || "Phiếu đề nghị thanh toán"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {data ? getStatusBadge(data.status) : null}
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
                aria-label="Đóng"
              >
                <X size={22} />
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {loading && (
              <div className="flex justify-center items-center h-64">
                <Loader2 size={32} className="animate-spin text-red-600" />
              </div>
            )}

            {!loading && !data && (
              <p className="text-red-500 text-sm font-medium text-center py-16">
                Không có dữ liệu
              </p>
            )}

            {!loading && data && (
              <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
                {/* LEFT: document paper */}
                <div className="min-h-0 max-h-[38vh] lg:max-h-none flex-none lg:flex-1 overflow-y-auto overscroll-contain bg-slate-100/80 border-b lg:border-b-0 lg:border-r border-slate-200 p-4 md:p-6">
                  <div className="mx-auto w-full max-w-[640px] pb-4">
                    <PaymentDocumentPaper data={data} lines={lines} />
                  </div>
                </div>

                {/* RIGHT: info + actions */}
                <div className="min-h-0 flex-1 lg:max-w-md lg:flex-none lg:w-[380px] xl:w-[420px] flex flex-col overflow-hidden bg-white">
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 md:px-5 md:py-5 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="h-11 w-11 rounded-2xl bg-red-50 text-red-700 flex items-center justify-center shrink-0 border border-red-100">
                        <User size={20} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-black text-slate-800 truncate">
                          {data.employee_name}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          {[data.branch_name, data.department_name]
                            .filter(Boolean)
                            .join(" • ") || "—"}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-center">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Số tiền thanh toán
                      </p>
                      <p className="text-2xl font-black text-red-700 mt-1 tracking-tight">
                        {Number(data.payment_amount || 0).toLocaleString("vi-VN")} ₫
                      </p>
                    </div>

                    {data.advance_request_id ? (
                      <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 space-y-2">
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                          Đối chiếu tạm ứng
                        </p>
                        <InfoRow
                          label="Đã tạm ứng"
                          value={`${Number(data.advance_amount || 0).toLocaleString("vi-VN")} ₫`}
                        />
                        <InfoRow
                          label="Kết quả"
                          value={`${data.settlement_label || "Khớp tạm ứng"}${
                            data.settlement_amount != null
                              ? ` - ${Number(data.settlement_amount || 0).toLocaleString("vi-VN")} ₫`
                              : ""
                          }`}
                        />
                      </div>
                    ) : null}

                    <div className="grid grid-cols-2 gap-3">
                      <InfoRow icon={FileText} label="Mã phiếu" value={code} />
                      <InfoRow
                        icon={Calendar}
                        label="Ngày chứng từ"
                        value={
                          data.payment_date
                            ? formatDateVN(data.payment_date)
                            : formatDateVN(data.created_at)
                        }
                      />
                      <InfoRow
                        label="Cấp duyệt"
                        value={
                          data.current_level != null
                            ? `Cấp ${data.current_level}`
                            : data.status === "draft"
                              ? "Chưa gửi"
                              : "—"
                        }
                      />
                    </div>

                    {(data.bank_name || data.account_number || data.account_holder) && (
                      <div className="rounded-2xl border border-slate-100 p-4 space-y-1.5">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                          <Landmark size={12} /> Nhận tiền
                        </p>
                        <p className="text-sm text-slate-700">
                          <span className="font-semibold">NH:</span>{" "}
                          {data.bank_name || "—"}
                        </p>
                        <p className="text-sm text-slate-700 font-mono">
                          <span className="font-semibold font-sans">STK:</span>{" "}
                          {data.account_number || "—"}
                        </p>
                        {data.account_holder ? (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Chủ TK:</span>{" "}
                            {data.account_holder}
                          </p>
                        ) : null}
                      </div>
                    )}

                    <div className="rounded-2xl border border-slate-100 p-4 space-y-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                        <Receipt size={12} /> Hạng mục
                      </p>
                      {lines.length > 0 ? (
                        <div className="space-y-2">
                          {lines.map((line, index) => (
                            <div
                              key={line.id || `${index}-${line.line_no || 0}`}
                              className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5"
                            >
                              <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                  Hạng mục {index + 1}
                                </p>
                                <p className="text-sm font-medium text-slate-800 mt-0.5">
                                  {line.description}
                                </p>
                              </div>
                              <p className="shrink-0 text-sm font-black text-red-700">
                                {Number(line.amount || 0).toLocaleString("vi-VN")} ₫
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">Không có hạng mục</p>
                      )}
                    </div>

                    {data.can_current_user_submit && (
                      <div className="p-4 rounded-2xl border border-red-100 bg-red-50/40">
                        <p className="text-xs text-slate-600">
                          Phiếu đang ở trạng thái <strong>Nháp</strong>. Gửi duyệt để
                          khởi tạo chuỗi phê duyệt theo quy tắc hệ thống.
                        </p>
                      </div>
                    )}

                    {data.approval_steps?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">
                          Lịch sử phê duyệt
                        </p>
                        <ApprovalTimeline steps={data.approval_steps} />
                      </div>
                    )}

                    {data.can_current_user_approve && (
                      <div>
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                          Lý do từ chối
                        </label>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          rows={3}
                          placeholder="Bắt buộc khi từ chối..."
                          className="w-full border border-slate-200 rounded-2xl p-3 text-sm font-medium focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                        />
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 border-t border-slate-100 bg-white px-4 py-3 md:px-5 md:py-4 safe-area-pb z-10">
                    <div className="flex flex-wrap items-center gap-1.5 justify-end">
                      {data.can_current_user_submit && (
                        <button
                          type="button"
                          title="Gửi duyệt"
                          disabled={actionLoading}
                          onClick={handleSubmitDraft}
                          className="inline-flex h-10 items-center gap-1.5 px-3 rounded-xl font-bold bg-red-700 text-white hover:bg-red-800 transition-all text-xs tracking-wide disabled:opacity-50 shrink-0 shadow-md shadow-red-100"
                        >
                          {actionLoading ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Send size={16} />
                          )}
                          Gửi duyệt
                        </button>
                      )}
                      {canCurrentUserRemind && (
                        <IconActionButton
                          title="Nhắc duyệt"
                          disabled={remindLoading}
                          onClick={handleRemind}
                          className="border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100"
                        >
                          {remindLoading ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <BellRing size={18} />
                          )}
                        </IconActionButton>
                      )}
                      {data.can_current_user_approve && (
                        <>
                          <IconActionButton
                            title="Từ chối"
                            disabled={actionLoading}
                            onClick={handleReject}
                            className="border-red-200 text-red-600 bg-red-50 hover:bg-red-100"
                          >
                            {actionLoading ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <XCircle size={18} />
                            )}
                          </IconActionButton>
                          <button
                            type="button"
                            title="Phê duyệt"
                            disabled={actionLoading}
                            onClick={handleApprove}
                            className="inline-flex h-10 items-center gap-1.5 px-3 rounded-xl font-bold bg-red-700 text-white hover:bg-red-800 transition-all text-xs tracking-wide disabled:opacity-50 shrink-0 shadow-md shadow-red-100"
                          >
                            {actionLoading ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Check size={16} />
                            )}
                            Duyệt
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

const IconActionButton = ({
  title,
  onClick,
  disabled,
  className = "",
  children,
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border bg-white transition-all disabled:opacity-50 shrink-0 ${className}`}
  >
    {children}
  </button>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start justify-between gap-3 rounded-xl border-2 border-slate-200 bg-white px-3.5 py-3">
    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 shrink-0 max-w-[42%]">
      {Icon ? <Icon size={12} className="shrink-0" /> : null}
      <span>{label}</span>
    </p>
    <p className="text-sm font-semibold text-slate-800 text-right break-words min-w-0 flex-1">
      {value || "—"}
    </p>
  </div>
);

export default PaymentDetailModal;
